import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Printer, Loader2, CheckCircle2, Filter, X, Clock, FileText, Plus, MessageSquare, Lock, Search } from 'lucide-react';
import { EmptyState, Stat } from './_shared';
import { useAuth } from '../../../context/AuthContext';
import { DeliveryDetailsModal } from './ActionsReportTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// تنسيق ISO datetime إلى YYYY-MM-DD (تاريخ الاعتماد)
const fmtApprovedDate = (iso) => {
  if (!iso) return '—';
  try {
    return String(iso).slice(0, 10);
  } catch {
    return '—';
  }
};

// ===== إجراءات الاستلامات: تكوين شارات الحالة =====
const STATUS_META = {
  pending: { label: 'معلَّق', cls: 'bg-yellow-100 text-yellow-800 border border-yellow-300' },
  in_progress: { label: 'قيد المعالجة', cls: 'bg-blue-100 text-blue-800 border border-blue-300' },
  resolved: { label: 'محلول', cls: 'bg-green-100 text-green-800 border border-green-300' },
  escalated: { label: 'مُصعَّد', cls: 'bg-red-100 text-red-800 border border-red-300' },
};

// عمود "أيام التأخير": لون حسب العتبة (7 أيام إنذار)
function DaysDelayedBadge({ days }) {
  if (days === null || days === undefined) return <span className="text-gray-400">—</span>;
  const cls = days >= 14 ? 'bg-red-600 text-white' :
              days >= 7 ? 'bg-orange-500 text-white' :
              days >= 3 ? 'bg-yellow-300 text-yellow-900' :
              'bg-green-100 text-green-700';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${cls}`}>
      <Clock className="w-3 h-3" /> {days}
    </span>
  );
}

// ===== Modal إنشاء/تعديل الإجراء + Timeline =====
function ActionModal({ token, row, canEdit = true, onClose, onSaved }) {
  const r = row?.receipt || {};
  const existing = row?.action;
  const [status, setStatus] = useState(existing?.status || 'pending');
  const [notes, setNotes] = useState(existing?.notes || '');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const history = existing?.history || [];
  
  const save = async () => {
    if (!canEdit) { toast.error('ليس لديك صلاحية تعديل الإجراءات'); return; }
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/invoice-matching/receipt-actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          delivery_id: row.delivery_id,
          receipt_number: r.receipt_number,
          supplier_name: row.supplier_name || '',
          status,
          notes,
        }),
      });
      if (!res.ok) {
        if (res.status === 403) toast.error('ليس لديك صلاحية تعديل الإجراءات');
        else toast.error('فشل الحفظ');
        return;
      }
      const result = await res.json();
      // ملاحظة إضافية إن وُجدت
      if (newNote.trim() && result.id) {
        await fetch(`${API_URL}/api/invoice-matching/receipt-actions/${result.id}/note`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ note: newNote.trim() }),
        });
      }
      toast.success(result.created ? 'تم إنشاء الإجراء' : 'تم تحديث الإجراء');
      onSaved();
    } finally {
      setSaving(false);
    }
  };
  
  const handlePrintAction = () => {
    const today = new Date().toLocaleDateString('ar-EG-u-nu-latn', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    const statusLabel = STATUS_META[status]?.label || status;
    const statusColor = STATUS_META[status]?.cls?.includes('yellow') ? '#ca8a04'
      : STATUS_META[status]?.cls?.includes('blue') ? '#2563eb'
      : STATUS_META[status]?.cls?.includes('green') ? '#16a34a'
      : STATUS_META[status]?.cls?.includes('red') ? '#dc2626' : '#6b7280';
    const sortedHistory = [...history].sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
    const timelineRows = sortedHistory.length ? sortedHistory.map((h, i) => {
      const dt = String(h.at || '').slice(0, 16).replace('T', ' ');
      let detail = '';
      if (h.action === 'auto_resolve_by_linking') {
        detail = h.note || 'حُلَّ تلقائياً بربط فاتورة';
      } else {
        if (h.action === 'create' || h.action === 'auto_create') detail = h.action === 'auto_create' ? 'إنشاء تلقائي بواسطة النظام' : 'إنشاء الإجراء';
        if (h.status_to) {
          const lbl = STATUS_META[h.status_to]?.label || h.status_to;
          const from = h.status_from ? ` (من ${STATUS_META[h.status_from]?.label || h.status_from})` : '';
          detail += (detail ? ' — ' : '') + `تغيير الحالة إلى: ${lbl}${from}`;
        }
        if (h.notes_changed) detail += (detail ? ' — ' : '') + 'تعديل تفاصيل الإجراء';
        if (h.note) detail += (detail ? ' | ملاحظة: ' : '') + h.note;
      }
      return `<tr>
        <td style="padding:6px;border:1px solid #ddd;text-align:center;width:40px">${i + 1}</td>
        <td style="padding:6px;border:1px solid #ddd;white-space:nowrap;width:130px;color:#6b7280;font-size:11px">${dt}</td>
        <td style="padding:6px;border:1px solid #ddd;font-weight:bold;color:#7c3aed;width:140px">${(h.by_user_name || '—').replace(/</g, '&lt;')}</td>
        <td style="padding:6px;border:1px solid #ddd">${(detail || '—').replace(/</g, '&lt;')}</td>
      </tr>`;
    }).join('') : `<tr><td colspan="4" style="padding:14px;text-align:center;color:#9ca3af">لا توجد سجلات في Timeline</td></tr>`;
    
    const createdInfo = existing ? `
      <tr><td>أنشئ بواسطة</td><td><strong>${existing.created_by_name || '—'}</strong></td></tr>
      <tr><td>تاريخ الإنشاء</td><td>${String(existing.created_at || '').slice(0, 16).replace('T', ' ')}</td></tr>
      ${existing.auto_created ? `<tr><td>المصدر</td><td><span style="background:#eef2ff;color:#4338ca;padding:2px 8px;border-radius:9999px;font-weight:bold">كشف تلقائي بواسطة النظام</span></td></tr>` : ''}
      ${existing.resolved_by_name ? `<tr><td>حُلَّ بواسطة</td><td><strong>${existing.resolved_by_name}</strong> — ${String(existing.resolved_at || '').slice(0,16).replace('T',' ')}</td></tr>` : ''}
    ` : '';
    
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>إجراء الاستلام #${r.receipt_number || ''}</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; direction: rtl; color:#111827; }
  h1 { font-size: 18px; margin: 0 0 4px; color:#7c3aed; }
  h2 { font-size: 14px; margin: 14px 0 6px; color:#4b5563; border-bottom:2px solid #e5e7eb; padding-bottom:4px; }
  .meta { font-size: 11px; color: #6b7280; margin-bottom: 10px; }
  .info { width:100%; border-collapse: collapse; font-size: 12px; margin-bottom:8px; }
  .info td { padding: 5px 8px; border:1px solid #e5e7eb; }
  .info td:first-child { background:#f9fafb; font-weight:bold; color:#374151; width: 28%; }
  .status-pill { display:inline-block; padding:3px 12px; border-radius:9999px; color:#fff; font-weight:bold; font-size:11px; background:${statusColor}; }
  .notes-box { border:1px solid #e5e7eb; border-radius:8px; padding:10px; background:#fefce8; font-size:13px; min-height:50px; line-height:1.7; white-space:pre-wrap; }
  table.tl { width:100%; border-collapse: collapse; font-size:12px; margin-top:6px; }
  table.tl th { background:#f3f4f6; padding:6px; border:1px solid #ddd; text-align:right; font-weight:bold; font-size:11px; }
  table.tl tr { page-break-inside: avoid; }
  .footer { margin-top:18px; padding-top:8px; border-top:1px solid #e5e7eb; font-size:10px; color:#6b7280; text-align:center; }
</style></head><body>
<h1>إجراء الاستلام #${r.receipt_number || ''}</h1>
<div class="meta">تاريخ طباعة التقرير: ${today}</div>

<h2>معلومات الاستلام</h2>
<table class="info">
  <tr><td>رقم الاستلام</td><td><strong>${r.receipt_number || '—'}</strong></td></tr>
  <tr><td>المورد</td><td><strong>${(row.supplier_name || '—').replace(/</g, '&lt;')}</strong></td></tr>
  <tr><td>المشروع</td><td>${r.project_number || '—'}</td></tr>
  <tr><td>تاريخ الاستلام</td><td>${r.receipt_date || '—'}</td></tr>
  <tr><td>أيام التأخير</td><td><span style="background:#dc2626;color:#fff;padding:2px 10px;border-radius:9999px;font-weight:bold">${row.days_delayed != null ? row.days_delayed + ' يوم' : '—'}</span></td></tr>
  <tr><td>المنشئ (للاستلام)</td><td>${(row.created_by || '—').replace(/</g, '&lt;')}</td></tr>
  <tr><td>المعتمِد</td><td>${(row.approved_by || '—').replace(/</g, '&lt;')}</td></tr>
</table>

<h2>تفاصيل الإجراء</h2>
<table class="info">
  <tr><td>الحالة الحالية</td><td><span class="status-pill">${statusLabel}</span></td></tr>
  ${createdInfo}
</table>

<h3 style="font-size:13px;margin:10px 0 4px;color:#374151">السبب / المسؤول / تفاصيل الإجراء</h3>
<div class="notes-box">${(notes || '— لا توجد تفاصيل —').replace(/</g, '&lt;')}</div>

<h2>سجل الإجراءات (Timeline) — ${sortedHistory.length} إدخال</h2>
<table class="tl">
  <thead><tr>
    <th style="width:40px">#</th>
    <th style="width:130px">التاريخ والوقت</th>
    <th style="width:140px">المستخدم</th>
    <th>التفاصيل</th>
  </tr></thead>
  <tbody>${timelineRows}</tbody>
</table>

<div class="footer">نظام إدارة المشاريع — تطوير محمد مهدي</div>
<script>window.onload = () => { setTimeout(() => { window.print(); }, 200); };</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=900,height=900');
    if (!w) { toast.error('السماح بالنوافذ المنبثقة مطلوب للطباعة'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="action-modal">
        <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold">إجراء الاستلام #{r.receipt_number}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          {/* معلومات الاستلام */}
          <div className="bg-gray-50 border rounded-lg p-3 grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-500">المورد:</span> <strong>{row.supplier_name}</strong></div>
            <div><span className="text-gray-500">المشروع:</span> {r.project_number || '—'}</div>
            <div><span className="text-gray-500">تاريخ الاستلام:</span> {r.receipt_date || '—'}</div>
            <div><span className="text-gray-500">أيام التأخير:</span> <DaysDelayedBadge days={row.days_delayed} /></div>
            <div><span className="text-gray-500">المنشئ:</span> {row.created_by}</div>
            <div><span className="text-gray-500">المعتمِد:</span> {row.approved_by || '—'}</div>
          </div>
          {/* النموذج */}
          <div className="space-y-3">
            {!canEdit && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-2 flex items-center gap-2" data-testid="readonly-banner">
                <Lock className="w-3.5 h-3.5" /> عرض فقط — ليس لديك صلاحية تعديل الإجراءات
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">الحالة</label>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <button key={k} type="button" data-testid={`status-${k}`} disabled={!canEdit} onClick={() => canEdit && setStatus(k)} className={`px-3 py-1.5 rounded-full text-xs font-bold ${status === k ? v.cls + ' ring-2 ring-offset-1' : 'bg-gray-100 text-gray-600'} ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">السبب / المسؤول / تفاصيل الإجراء</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} readOnly={!canEdit} disabled={!canEdit} placeholder="مثال: لا توجد صور بالموقع — المسؤول: أحمد من المستودع — جارٍ المتابعة..." data-testid="action-notes" className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm ${!canEdit ? 'bg-gray-50 text-gray-600' : ''}`} />
            </div>
            {existing && canEdit && (
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">إضافة ملاحظة جديدة لـ Timeline</label>
                <div className="flex gap-2">
                  <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="ملاحظة..." data-testid="new-note" className="flex-1 h-10 px-3 border rounded-lg outline-none text-sm" />
                </div>
              </div>
            )}
          </div>
          {/* Timeline */}
          {history.length > 0 && (
            <div>
              <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-purple-600" /> سجل الإجراءات (Timeline)</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {[...history].reverse().map((h, i) => (
                  <div key={i} className="border-r-2 border-purple-300 pr-3 text-xs bg-purple-50/40 p-2 rounded">
                    <div className="flex justify-between text-gray-500">
                      <span className="font-bold text-purple-700">{h.by_user_name}</span>
                      <span>{String(h.at || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    {h.status_to && <div className="mt-1">حالة: <span className="font-bold">{STATUS_META[h.status_to]?.label || h.status_to}</span>{h.status_from && <span className="text-gray-400"> (من {STATUS_META[h.status_from]?.label || h.status_from})</span>}</div>}
                    {h.note && <div className="mt-1 text-gray-700">{h.note}</div>}
                    {h.notes_changed && <div className="mt-1 text-gray-400 italic">(تم تعديل تفاصيل الإجراء)</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="border-t p-3 flex justify-between items-center gap-2 flex-wrap">
          <button onClick={handlePrintAction} data-testid="print-single-action" className="px-3 py-2 border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-lg text-sm font-bold flex items-center gap-2">
            <Printer className="w-4 h-4" /> طباعة الإجراء
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50">إغلاق</button>
            {canEdit && (
              <button onClick={save} disabled={saving} data-testid="save-action" className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg text-sm font-bold flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {existing ? 'تحديث الإجراء' : 'إنشاء الإجراء'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
export function ReportTab({ token, onPrint }) {
  const { user } = useAuth();
  // فحص صلاحية إنشاء/تعديل إجراءات الاستلامات
  const canEditActions = (() => {
    if (!user) return false;
    if (user.role === 'super_admin' || user.role === 'company_admin') return true;
    const perms = user.permissions || [];
    if (perms.includes('all') || perms.includes('proc_receipt_actions')) return true;
    return false;
  })();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionRow, setActionRow] = useState(null); // row being acted upon
  // ===== الفرز =====
  const [sortBy, setSortBy] = useState('default'); // 'default' | 'days_desc' | 'days_asc'
  // ===== الفلاتر =====
  const [supplier, setSupplier] = useState('');
  const [project, setProject] = useState('');
  const [approverId, setApproverId] = useState('');
  const [creatorId, setCreatorId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [minDays, setMinDays] = useState('');
  const [receiptSearch, setReceiptSearch] = useState('');
  const [deliveryModal, setDeliveryModal] = useState(null); // { delivery_id, receipt_number }

  const activeFiltersCount =
    (supplier ? 1 : 0) +
    (project ? 1 : 0) +
    (approverId ? 1 : 0) +
    (creatorId ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    (minDays ? 1 : 0) +
    (receiptSearch.trim() ? 1 : 0);
  
  // فرز + فلترة الصفوف بناءً على sortBy و minDays و receiptSearch
  const sortedItems = React.useMemo(() => {
    let items = data?.items || [];
    // فلترة بالحد الأدنى لأيام التأخير
    const md = parseInt(minDays, 10);
    if (md && md > 0) {
      items = items.filter(it => it.days_delayed != null && it.days_delayed >= md);
    }
    // فلترة برقم الاستلام
    const rq = (receiptSearch || '').trim().toLowerCase();
    if (rq) {
      items = items.filter(it => String(it.receipt?.receipt_number || '').toLowerCase().includes(rq));
    }
    if (sortBy === 'default') return items;
    const arr = [...items];
    const dir = sortBy === 'days_desc' ? -1 : 1;
    arr.sort((a, b) => {
      const da = a.days_delayed;
      const db = b.days_delayed;
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return (da - db) * dir;
    });
    return arr;
  }, [data, sortBy, minDays, receiptSearch]);

  const buildFilters = () => ({
    supplier: supplier || '',
    project: project || '',
    approver_id: approverId || '',
    creator_id: creatorId || '',
    date_from: dateFrom || '',
    date_to: dateTo || '',
    // فلاتر العميل (تُطبّق في PrintReportView)
    min_days: minDays || '',
    sort_by: sortBy || 'default',
  });

  const clearFilters = () => {
    setSupplier('');
    setProject('');
    setApproverId('');
    setCreatorId('');
    setDateFrom('');
    setDateTo('');
    setSortBy('default');
    setMinDays('');
  };

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (supplier) params.append('supplier', supplier);
        if (project) params.append('project', project);
        if (approverId) params.append('approver_id', approverId);
        if (creatorId) params.append('creator_id', creatorId);
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        const res = await fetch(`${API_URL}/api/invoice-matching/report/unlinked?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) setData(await res.json());
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [token, supplier, project, approverId, creatorId, dateFrom, dateTo]);

  const approvers = data?.available_approvers || [];
  const creators = data?.available_creators || [];

  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-800 flex items-center gap-2">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span>هذه الاستلامات معتمدة (تم الاستلام) لكن لم يتم ربطها بأي فاتورة من المورد بعد.</span>
      </div>

      {/* ===== شريط الإجراءات ===== */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <button
          onClick={() => setShowAdvanced((s) => !s)}
          data-testid="toggle-advanced-filters-btn"
          className={`flex-1 sm:flex-initial h-11 px-4 border rounded-lg flex items-center justify-center gap-2 font-medium whitespace-nowrap ${
            showAdvanced || activeFiltersCount > 0
              ? 'bg-purple-100 border-purple-300 text-purple-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" />
          فلترة متقدمة
          {activeFiltersCount > 0 && (
            <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full" data-testid="active-filters-count">
              {activeFiltersCount}
            </span>
          )}
        </button>
        <button
          onClick={() => onPrint(buildFilters())}
          data-testid="print-report-pdf-btn"
          className="flex-1 sm:flex-initial h-11 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2 font-medium whitespace-nowrap"
        >
          <Printer className="w-4 h-4" />
          طباعة PDF
        </button>
      </div>

      {/* ===== بحث سريع برقم الاستلام ===== */}
      <div className="bg-white border rounded-xl p-3 mb-3 flex items-center gap-2 shadow-sm">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 pointer-events-none" />
          <input
            type="text"
            value={receiptSearch}
            onChange={(e) => setReceiptSearch(e.target.value)}
            placeholder="🔍 ابحث برقم الاستلام (مثال: TESTRCPT-001 أو جزء منه)..."
            data-testid="report-receipt-search-input"
            className="w-full h-10 pr-10 pl-3 border-2 border-purple-200 rounded-lg text-sm bg-purple-50/30 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 focus:outline-none focus:bg-white"
          />
        </div>
        {receiptSearch && (
          <button
            type="button"
            onClick={() => setReceiptSearch('')}
            data-testid="clear-receipt-search-btn"
            className="h-10 px-3 border rounded-lg text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> مسح
          </button>
        )}
        {receiptSearch.trim() && (
          <div className="text-xs text-gray-500 whitespace-nowrap px-2">
            {sortedItems.length} نتيجة
          </div>
        )}
      </div>

      {/* ===== لوحة الفلترة المتقدمة (قابلة للطي) ===== */}
      {showAdvanced && (
        <div className="bg-gray-50 border rounded-xl p-3 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="advanced-filters-panel">
          <div>
            <label className="block text-xs text-gray-600 mb-1">المورد</label>
            <input
              type="text"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="ابحث باسم المورد..."
              data-testid="filter-supplier-input"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">المشروع</label>
            <input
              type="text"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder="ابحث برقم/اسم المشروع..."
              data-testid="filter-project-input"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">مدخل الكشف</label>
            <select
              value={creatorId}
              onChange={(e) => setCreatorId(e.target.value)}
              data-testid="filter-creator-select"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            >
              <option value="">كل المدخلين</option>
              {creators.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">المعتمِد (المشرف)</label>
            <select
              value={approverId}
              onChange={(e) => setApproverId(e.target.value)}
              data-testid="filter-approver-select"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            >
              <option value="">كل المشرفين</option>
              {approvers.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">تاريخ الاعتماد من</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="filter-date-from-input"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">تاريخ الاعتماد إلى</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="filter-date-to-input"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">ترتيب حسب أيام التأخير</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="filter-sort-days"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            >
              <option value="default">الترتيب الافتراضي (حسب رقم الكشف)</option>
              <option value="days_desc">الأكثر تأخيراً أولاً ▼</option>
              <option value="days_asc">الأقل تأخيراً أولاً ▲</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">إظهار فقط: تأخير ≥ يوم</label>
            <input
              type="number"
              min="0"
              placeholder="مثلاً: 7 أو 14"
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
              data-testid="filter-min-days"
              className="w-full h-11 px-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
            />
          </div>

          {activeFiltersCount > 0 && (
            <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
              <button
                onClick={clearFilters}
                data-testid="clear-filters-btn"
                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1 font-medium"
              >
                <X className="w-4 h-4" /> مسح كل الفلاتر
              </button>
            </div>
          )}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat label="استلامات بدون فواتير" value={data.total_unlinked} color="amber" />
          <Stat label="موردين متأثرين" value={data.suppliers_count} color="purple" />
          <Stat label="مستخدمين" value={data.users_count} color="gray" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>
      ) : !data || data.total_unlinked === 0 ? (
        <EmptyState icon={CheckCircle2} title="ممتاز!" subtitle="كل الاستلامات المعتمدة لها فواتير مرتبطة" />
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-amber-50 text-amber-900">
              <tr>
                <th className="px-2 py-2 text-right">كشف #</th>
                <th className="px-2 py-2 text-right">رقم الاستلام</th>
                <th className="px-2 py-2 text-right">تاريخ الاستلام</th>
                <th className="px-2 py-2 text-right bg-red-100 text-red-900">
                  <div className="inline-flex items-center gap-1">
                    أيام التأخير
                    {sortBy !== 'default' && (
                      <span className="text-[10px] opacity-70" data-testid="sort-active-indicator">
                        {sortBy === 'days_desc' ? '▼' : '▲'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-2 py-2 text-right">المورد</th>
                <th className="px-2 py-2 text-right">المشروع</th>
                <th className="px-2 py-2 text-right">المنشئ</th>
                <th className="px-2 py-2 text-right">المعتمِد</th>
                <th className="px-2 py-2 text-right">تاريخ الاعتماد</th>
                <th className="px-2 py-2 text-right bg-purple-100 text-purple-900">الإجراء</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((row, i) => {
                const r = row.receipt || {};
                const action = row.action;
                const meta = action ? STATUS_META[action.status] : null;
                return (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="px-2 py-2 font-bold">#{row.delivery_number}</td>
                    <td className="px-2 py-2 font-semibold">
                      <button
                        type="button"
                        onClick={() => setDeliveryModal({ delivery_id: row.delivery_id, receipt_number: r.receipt_number })}
                        data-testid={`open-delivery-details-${i}`}
                        title="انقر لعرض تفاصيل كشف الاستلام والمشروع"
                        className="text-purple-700 hover:text-purple-900 hover:underline text-right"
                      >
                        {r.receipt_number}
                      </button>
                    </td>
                    <td className="px-2 py-2">{r.receipt_date || '—'}</td>
                    <td className="px-2 py-2"><DaysDelayedBadge days={row.days_delayed} /></td>
                    <td className="px-2 py-2">{row.supplier_name}</td>
                    <td className="px-2 py-2">{r.project_number || '—'}</td>
                    <td className="px-2 py-2">{row.created_by}</td>
                    <td className="px-2 py-2 text-purple-700 font-medium">{row.approved_by || '—'}</td>
                    <td className="px-2 py-2 text-gray-700">{fmtApprovedDate(row.approved_at)}</td>
                    <td className="px-2 py-2">
                      {action ? (
                        <button onClick={() => setActionRow(row)} data-testid={`view-action-${i}`} className={`px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${meta?.cls}`}>
                          {meta?.label || action.status}
                        </button>
                      ) : canEditActions ? (
                        <button onClick={() => setActionRow(row)} data-testid={`add-action-${i}`} className="px-2 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center gap-1">
                          <Plus className="w-3 h-3" /> إضافة
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400" data-testid={`no-perm-${i}`}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {actionRow && (
        <ActionModal token={token} row={actionRow} canEdit={canEditActions} onClose={() => setActionRow(null)} onSaved={() => {
          setActionRow(null);
          // إعادة تحميل البيانات
          const params = new URLSearchParams();
          if (supplier) params.append('supplier', supplier);
          if (project) params.append('project', project);
          if (approverId) params.append('approver_id', approverId);
          if (creatorId) params.append('creator_id', creatorId);
          if (dateFrom) params.append('date_from', dateFrom);
          if (dateTo) params.append('date_to', dateTo);
          fetch(`${API_URL}/api/invoice-matching/report/unlinked?${params}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.ok ? r.json() : null).then(d => d && setData(d));
        }} />
      )}

      {deliveryModal && (
        <DeliveryDetailsModal
          token={token}
          deliveryId={deliveryModal.delivery_id}
          highlightReceipt={deliveryModal.receipt_number}
          onClose={() => setDeliveryModal(null)}
        />
      )}
    </div>
  );
}
